import { FC } from "react";
import {
  Box,
  Flex,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import { maskCurrency, maskPercent } from "@Utils/Masks/currency";

export type InputMask = "currency" | "percent" | "integer";

export interface AppInputProps extends Omit<ChakraInputProps, "onChange"> {
  label?: string;
  hintText?: string;
  hasError?: boolean;
  leftAddon?: string;
  rightAddon?: string;
  mask?: InputMask;
  onChange?: (value: string) => void;
}

export const AppInput: FC<AppInputProps> = ({
  label,
  hintText,
  hasError,
  leftAddon,
  rightAddon,
  mask,
  onChange,
  isRequired,
  value,
  ...props
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (mask === "currency") val = maskCurrency(val);
    else if (mask === "percent") val = maskPercent(val);
    else if (mask === "integer") val = val.replace(/\D/g, "");
    onChange?.(val);
  };

  const borderColor = hasError
    ? "states.error.base"
    : "neutrals.stroke.soft-200";

  return (
    <Box w="100%">
      {label && (
        <Flex gap="2px" alignItems="center" mb="4px">
          <Text
            fontSize="12px"
            fontWeight="500"
            color="neutrals.text.strong-950"
            fontFamily="body"
          >
            {label}
          </Text>
          {isRequired && (
            <Text fontSize="12px" color="primary.light.base">
              *
            </Text>
          )}
        </Flex>
      )}

      <InputGroup>
        {leftAddon && (
          <InputLeftElement pointerEvents="none">
            <Text fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
              {leftAddon}
            </Text>
          </InputLeftElement>
        )}

        <ChakraInput
          value={value}
          onChange={handleChange}
          borderColor={borderColor}
          _focus={{
            borderColor: "neutrals.text.strong-950",
            boxShadow: "none",
          }}
          _hover={{ borderColor: "neutrals.stroke.sub-300" }}
          bg="neutrals.background.white-0"
          fontSize="14px"
          fontFamily="body"
          pl={leftAddon ? "36px" : undefined}
          pr={rightAddon ? "36px" : undefined}
          {...props}
        />

        {rightAddon && (
          <InputRightElement pointerEvents="none">
            <Text fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
              {rightAddon}
            </Text>
          </InputRightElement>
        )}
      </InputGroup>

      {hintText && (
        <Text
          fontSize="11px"
          color={hasError ? "states.error.base" : "neutrals.text.soft-400"}
          mt="4px"
          fontFamily="body"
        >
          {hintText}
        </Text>
      )}
    </Box>
  );
};
